/**
 * My Work — the assignee's queue, and the intern's home screen.
 *
 * Backed by /service/my-work, which hard-wires the assignee to the caller's
 * token. There is no "whose work" control here because the endpoint does not
 * accept one.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type WorkItem, type WorkItemDetail } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2, Clock, AlertTriangle, Ban, ArrowLeft, Send, Timer, Paperclip,
} from 'lucide-react';

interface MyWorkSummary {
  due_today: number;
  overdue: number;
  awaiting_review: number;
  blocked: number;
  estimated_hours_remaining: number;
  hours_logged_today: number;
}

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  internal_review: 'In review',
  client_approval: 'With client',
  completed: 'Completed',
  blocked: 'Blocked',
  cancelled: 'Cancelled',
};

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'completed') return 'default';
  if (status === 'blocked' || status === 'cancelled') return 'destructive';
  if (status === 'internal_review' || status === 'client_approval') return 'secondary';
  return 'outline';
}

export function MyWorkPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  return openId
    ? <WorkDetail id={openId} onBack={() => setOpenId(null)} />
    : <WorkQueue onOpen={setOpenId} />;
}

// ---------------------------------------------------------------- queue

function WorkQueue({ onOpen }: { onOpen: (id: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'my-work'],
    queryFn: () => api.getMyWork(),
  });

  const items = (data?.data ?? []) as WorkItem[];
  const summary = (data as { summary?: MyWorkSummary } | undefined)?.summary;

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading your work…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-semibold tracking-tight">My Work Today</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Everything assigned to you across all clients.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Stat label="Due today" value={summary?.due_today ?? 0} icon={<Clock className="h-4 w-4" />} />
        <Stat label="Overdue" value={summary?.overdue ?? 0} icon={<AlertTriangle className="h-4 w-4" />} tone={(summary?.overdue ?? 0) > 0 ? 'bad' : undefined} />
        <Stat label="In review" value={summary?.awaiting_review ?? 0} icon={<Send className="h-4 w-4" />} />
        <Stat label="Blocked" value={summary?.blocked ?? 0} icon={<Ban className="h-4 w-4" />} tone={(summary?.blocked ?? 0) > 0 ? 'bad' : undefined} />
        <Stat label="Est. remaining" value={`${Number(summary?.estimated_hours_remaining ?? 0).toFixed(2)}h`} icon={<Timer className="h-4 w-4" />} />
        <Stat label="Logged today" value={`${Number(summary?.hours_logged_today ?? 0).toFixed(2)}h`} icon={<CheckCircle2 className="h-4 w-4" />} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Assigned to you</CardTitle></CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              Nothing assigned to you right now.
            </p>
          ) : (
            <ul className="divide-y">
              {items.map((w) => (
                <li key={w.id}>
                  <button
                    onClick={() => onOpen(w.id)}
                    className="w-full text-left px-6 py-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{w.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {w.account_name} · {w.service_line_name} · {w.cycle_label}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {w.is_overdue && <Badge variant="destructive">Overdue</Badge>}
                        <Badge variant={statusVariant(w.status)}>
                          {STATUS_LABEL[w.status] ?? w.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground w-24 text-right">
                          {w.due_date ? formatDate(w.due_date) : '—'}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, icon, tone }: {
  label: string; value: React.ReactNode; icon: React.ReactNode; tone?: 'bad';
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className={tone === 'bad' ? 'text-destructive' : 'text-muted-foreground'}>{icon}</span>
        </div>
        <p className={`text-2xl font-semibold mt-1 ${tone === 'bad' ? 'text-destructive' : ''}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------- detail

function WorkDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [minutes, setMinutes] = useState('');
  const [timeNote, setTimeNote] = useState('');
  const [clientNotes, setClientNotes] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'work-item', id],
    queryFn: () => api.getWorkItem(id),
  });
  const item = data?.data as WorkItemDetail | undefined;

  const logTime = useMutation({
    mutationFn: () =>
      api.createServiceTimeEntry({
        workItemId: id,
        engagementId: item?.engagement_id,
        cycleId: item?.cycle_id,
        workDate: new Date().toISOString().slice(0, 10),
        durationMinutes: Number(minutes),
        // Always logged as covered by the subscription. Separately billable
        // time requires an approved authorization, which the server enforces —
        // so it is never something this screen can choose on its own.
        billableClass: 'included',
        description: timeNote || null,
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not log time', description: r.error?.message, variant: 'destructive' });
        return;
      }
      setMinutes(''); setTimeNote('');
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Time logged' });
    },
  });

  const submit = useMutation({
    mutationFn: () => api.submitWorkItem(id, { clientNotes: clientNotes || null }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not submit', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({
        title: r.data?.status === 'completed' ? 'Completed' : 'Submitted for review',
      });
      onBack();
    },
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!item) return <div className="p-6 text-sm text-muted-foreground">Work item not found.</div>;

  const evidence: string[] = (() => {
    try { return item.evidence_required ? JSON.parse(item.evidence_required) : []; }
    catch { return []; }
  })();

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to my work
      </Button>

      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-serif font-semibold tracking-tight">{item.title}</h1>
          <Badge variant={statusVariant(item.status)}>{STATUS_LABEL[item.status] ?? item.status}</Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {item.account_name} · {item.service_line_name} · {item.cycle_label}
          {item.due_date ? ` · due ${formatDate(item.due_date)}` : ''}
        </p>
      </div>

      {item.review_outcome && item.review_outcome !== 'approved' && (
        <Card className="border-destructive/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-destructive">Returned for revision</CardTitle>
          </CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap">
            {item.review_comment || 'No comment was left.'}
          </CardContent>
        </Card>
      )}

      {item.instructions && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Instructions</CardTitle></CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap">{item.instructions}</CardContent>
        </Card>
      )}

      {/* Client context, reachable because this item is assigned to the caller. */}
      {item.profile.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Client details</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
              {item.profile.map((f) => (
                <div key={f.field_key}>
                  <dt className="text-xs text-muted-foreground">{f.label ?? f.field_key}</dt>
                  <dd className="break-words">{f.field_value || '—'}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      {(item.acceptance_criteria || evidence.length > 0) && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">What "done" means</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {item.acceptance_criteria && (
              <p className="whitespace-pre-wrap">{item.acceptance_criteria}</p>
            )}
            {evidence.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Evidence required</p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {evidence.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Log time
            <span className="ml-2 font-normal text-xs text-muted-foreground">
              {item.estimated_hours != null && `estimated ${item.estimated_hours}h · `}
              logged {Number(item.actual_hours ?? 0).toFixed(2)}h
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[140px_1fr_auto] sm:items-end">
            <div>
              <Label htmlFor="mins" className="text-xs">Minutes</Label>
              <Input
                id="mins" type="number" min={1} max={1440} value={minutes}
                onChange={(e) => setMinutes(e.target.value)} placeholder="30"
              />
            </div>
            <div>
              <Label htmlFor="note" className="text-xs">What you did</Label>
              <Input
                id="note" value={timeNote} onChange={(e) => setTimeNote(e.target.value)}
                placeholder="Updated the page title and meta description"
              />
            </div>
            <Button
              onClick={() => logTime.mutate()}
              disabled={!minutes || Number(minutes) <= 0 || logTime.isPending}
            >
              Log
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Recorded in minutes. Entries go to your manager for approval.
          </p>
        </CardContent>
      </Card>

      <EvidenceCard item={item} />

      {item.status !== 'completed' && item.status !== 'cancelled' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {item.requires_internal_review ? 'Submit for review' : 'Mark complete'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="cn" className="text-xs">
                Summary for the client <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="cn" rows={3} value={clientNotes}
                onChange={(e) => setClientNotes(e.target.value)}
                placeholder="Describe the change in plain language — this may appear in the monthly report."
              />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
                <Send className="h-4 w-4 mr-1.5" />
                {item.requires_internal_review ? 'Submit for review' : 'Mark complete'}
              </Button>
              {item.requires_internal_review && (
                <p className="text-xs text-muted-foreground">
                  Your manager reviews this before anything reaches the client.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Evidence for a work item.
 *
 * The plan item lists what evidence is required; this is where it gets
 * attached. Before/after URLs and a written summary cover most SEO work
 * without needing a file upload, which keeps the common case to one screen.
 */
function EvidenceCard({ item }: { item: WorkItemDetail }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [urlBefore, setUrlBefore] = useState('');
  const [urlAfter, setUrlAfter] = useState('');
  const [summary, setSummary] = useState('');
  const [keywords, setKeywords] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.createServiceDeliverable({
        workItemId: item.id,
        name: name || 'Evidence',
        deliverableType: urlAfter ? 'page_update' : 'other',
        urlBefore: urlBefore || null,
        urlAfter: urlAfter || null,
        changeSummary: summary || null,
        keywordsAddressed: keywords
          ? keywords.split(',').map((k) => k.trim()).filter(Boolean)
          : undefined,
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not attach', description: r.error?.message, variant: 'destructive' });
        return;
      }
      setName(''); setUrlBefore(''); setUrlAfter(''); setSummary(''); setKeywords('');
      setAdding(false);
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Evidence attached' });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteServiceDeliverable(id),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not remove', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
    },
  });

  const locked = item.status === 'completed' || item.status === 'client_approval';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Evidence
          <span className="ml-2 font-normal text-xs text-muted-foreground">
            {item.deliverables.length} attached
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {item.deliverables.length > 0 && (
          <ul className="divide-y rounded-md border">
            {item.deliverables.map((d) => (
              <li key={d.id} className="px-3 py-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm">
                  <p className="font-medium">{d.name}</p>
                  {d.url_after && (
                    <p className="text-xs text-muted-foreground break-all">
                      {d.url_before ? `${d.url_before} → ` : ''}{d.url_after}
                    </p>
                  )}
                  {d.change_summary && (
                    <p className="text-xs text-muted-foreground mt-0.5">{d.change_summary}</p>
                  )}
                </div>
                {!locked && (
                  <Button
                    size="sm" variant="ghost"
                    className="text-muted-foreground shrink-0"
                    onClick={() => remove.mutate(d.id)}
                    disabled={remove.isPending}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {locked ? (
          <p className="text-xs text-muted-foreground">
            This work is approved or with the client, so its evidence is now part of the record.
          </p>
        ) : adding ? (
          <div className="space-y-3 rounded-md border p-3">
            <div>
              <Label className="text-xs">What is this?</Label>
              <Input
                className="mt-1" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Commercial towing page — title and meta updated"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Before URL</Label>
                <Input className="mt-1" value={urlBefore} onChange={(e) => setUrlBefore(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">After URL</Label>
                <Input className="mt-1" value={urlAfter} onChange={(e) => setUrlAfter(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Summary of changes</Label>
              <Textarea
                className="mt-1" rows={2} value={summary}
                onChange={(e) => setSummary(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Keywords addressed</Label>
              <Input
                className="mt-1" value={keywords} onChange={(e) => setKeywords(e.target.value)}
                placeholder="comma, separated"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => add.mutate()} disabled={add.isPending}>
                Attach
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Paperclip className="h-4 w-4 mr-1.5" /> Attach evidence
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
